"use client"

import { useDictionary } from "@/core/hooks/use-dictionary";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import TextInput from "../../input/TextInput";
import { Heading } from "@/components/ui/Heading";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import FileUploadInput from "../../input/MultipleFileUploadInput";

interface ProductSceneWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const emptyProductSceneItem = () => ({
    sceneImage: undefined,
    productImage: undefined,
    title: '', title_en: '', title_ar: '',
    link: '',
});

export const ProductSceneWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: ProductSceneWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof key === 'string' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const removeItem = (itemIndex: number) => {
        if (fields.length <= 1) return;
        remove(itemIndex);
    };

    const renderImageField = (itemIndex: number, field: 'sceneImage' | 'productImage', label: string) => {
        const path = `${name}.${widgetIndex}.items.${itemIndex}.${field}`;
        return (
            <div className="space-y-1">
                <label className="text-xs text-primary-600">{label}</label>
                <Controller
                    control={control}
                    name={path}
                    render={({ field }) => (
                        <FileUploadInput
                            accept="image/*"
                            disabled={disabled}
                            onUploadSuccess={(image) => {
                                if (!Array.isArray(image) && image) field.onChange(image);
                            }}
                            defaultImages={
                                field.value
                                    ? [{ ...field.value, name: field.value.name || '', description: field.value.description || '' }]
                                    : []
                            }
                            onDefaultRemove={() => field.onChange(undefined)}
                            error={getError(path) as string | undefined}
                        />
                    )}
                />
            </div>
        );
    };

    return (
        <>
            <MultilingualInput
                baseName={`${name}.${widgetIndex}.title`}
                label={dictionary.forms?.title}
                register={register}
                errors={errors}
                disabled={disabled}
            />

            <MultilingualTextareaInput
                baseName={`${name}.${widgetIndex}.description`}
                label={dictionary.forms?.description}
                register={register}
                errors={errors}
                disabled={disabled}
            />

            {fields.map((field, itemIndex) => (
                <div key={field.id} className="border col-span-2 p-4 rounded-md space-y-4 bg-background">
                    <div className="flex justify-between items-center">
                        <Heading level={4}>
                            ردیف {itemIndex + 1}
                            <span className="text-xs text-primary-500 font-normal mr-2">
                                ({itemIndex % 2 === 0 ? 'عکس محیطی سمت راست' : 'عکس محیطی سمت چپ'})
                            </span>
                        </Heading>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(itemIndex)}
                            disabled={fields.length <= 1 || disabled}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderImageField(itemIndex, 'sceneImage', 'عکس محیطی (نسبت پیشنهادی ۳:۲، افقی)')}
                        {renderImageField(itemIndex, 'productImage', 'عکس محصول (بدون پس‌زمینه، افقی)')}
                    </div>

                    <MultilingualInput
                        baseName={`${name}.${widgetIndex}.items.${itemIndex}.title`}
                        label="نام محصول"
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />

                    <TextInput
                        name={`${name}.${widgetIndex}.items.${itemIndex}.link`}
                        label={dictionary.forms?.link}
                        placeholder={dictionary.forms?.link}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                </div>
            ))}

            <Button type="button" variant="outline" onClick={() => append(emptyProductSceneItem())} disabled={disabled} className="mt-2">
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};
